import React, { useEffect, useState } from "react";
import { Icon, D } from "./icons";
import { BODY_GROUPS, CATEGORY_OPTIONS, CAPACITY_OPTIONS, VOLUME_OPTIONS } from "./catalog";
import { ORDER_TYPES } from "../loads/common";
import DateRangeField from "./DateRangeField";

/**
 * The search window of «Главная» and «Поиск» (transport and loads). No field is required — any
 * combination (including none) is a valid search. The page owns `form` (see EMPTY_FILTERS),
 * `extras` (the «Дополнительные фильтры» toggle) and a <datalist id="city-options">.
 *
 * Which field sits where follows the two designs:
 * - layout="home" (iTruck_Home_Responsive): Откуда · Радиус · Куда / Категория авто · Тип кузова · Когда свободен;
 *   «Дополнительные фильтры»: Грузоподъёмность · Объём · Ставка от · Только проверенные.
 * - layout="search" (iTruck_Search_Unified): Откуда · Радиус подачи · Куда / Тип груза (loads) or Тип кузова (transport) ·
 *   Категория транспорта · Грузоподъёмность / Объём · Когда свободен · Ставка от. No extra fields.
 */
const narrow = () => typeof window !== "undefined" && !!window.matchMedia?.("(max-width: 767px)").matches;

export default function SearchFields({ form, setForm, kind = "trucks", layout = "search", extras = false }) {
  // phones put Откуда | Куда side by side: the long placeholders would be cut
  const [phone, setPhone] = useState(narrow);
  useEffect(() => {
    const mq = window.matchMedia?.("(max-width: 767px)");
    if (!mq) return undefined;
    const on = () => setPhone(mq.matches);
    mq.addEventListener?.("change", on);
    return () => mq.removeEventListener?.("change", on);
  }, []);
  const loads = kind === "loads";
  const home = layout === "home";
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const clear = (field) =>
    form[field] ? (
      <button
        type="button"
        className="clear"
        aria-label={field === "origin" ? "Очистить город отправления" : "Очистить город назначения"}
        onClick={() => setForm((f) => ({ ...f, [field]: "" }))}
      >
        <Icon d={D.close} />
      </button>
    ) : null;

  const select = (name, label, value, options, icon = D.chevron) => (
    <label className="field" key={name}>
      <span>{label}</span>
      <span className="select-wrap">
        <select name={name} aria-label={label} value={value} onChange={set(name)}>
          {options}
        </select>
        <Icon d={icon} />
      </span>
    </label>
  );

  const origin = (
    <label className="field" key="origin">
      <span>Откуда</span>
      <span className="input-wrap location blue">
        <Icon d={D.pin} />
        <input name="origin" list="city-options" placeholder={phone ? "Город" : home ? "Выберите город" : "Любой город"} aria-label="Откуда" value={form.origin} onChange={set("origin")} />
        {clear("origin")}
      </span>
    </label>
  );
  const destination = (
    <label className="field" key="destination">
      <span>Куда</span>
      <span className="input-wrap location red">
        <Icon d={D.pin} />
        <input name="destination" list="city-options" placeholder={phone ? "Город" : home ? "Укажите город" : "Любое направление"} aria-label="Куда" value={form.destination} onChange={set("destination")} />
        {clear("destination")}
      </span>
    </label>
  );
  const radius = select(
    "radius",
    home ? "Радиус" : "Радиус подачи",
    form.radius,
    [
      <option key="all" value="all">Любой</option>,
      ...[50, 100, 200, 500].map((r) => (
        <option key={r} value={String(r)}>
          {r} км
        </option>
      )),
    ],
  );
  const category = select("category", home ? "Категория авто" : "Категория транспорта", form.category, [
    <option key="any" value="Любая">
      {home ? "Любая категория" : "Любая"}
    </option>,
    ...CATEGORY_OPTIONS.map((c) => (
      <option key={c.value} value={c.value}>
        {c.label}
      </option>
    )),
  ]);
  const body = select("body", "Тип кузова", form.body, [
    <option key="any" value="Любой">Любой</option>,
    ...BODY_GROUPS.map((g) => (
      <optgroup key={g.id} label={g.name}>
        <option value={g.name}>{g.name} — все</option>
        {g.kinds.map((k) => (
          <option key={k.id} value={k.name}>
            {k.name}
          </option>
        ))}
      </optgroup>
    )),
  ]);
  const cargo = select("cargo", "Тип груза", form.cargo, [
    <option key="all" value="all">Любой</option>,
    ...Object.entries(ORDER_TYPES).map(([id, name]) => (
      <option key={id} value={id}>
        {name}
      </option>
    )),
  ]);
  const capacity = select(
    "capacity",
    loads ? "Вес груза, т" : "Грузоподъёмность, т",
    form.capacity,
    CAPACITY_OPTIONS.map((o) => (
      <option key={o.value} value={o.value}>
        {o.label}
      </option>
    )),
  );
  const volume = select(
    "volume",
    loads ? "Объём груза, м³" : "Объём кузова, м³",
    form.volume,
    VOLUME_OPTIONS.map((o) => (
      <option key={o.value} value={o.value}>
        {o.label}
      </option>
    )),
  );
  const available = <DateRangeField key="dates" label={loads ? "Дата погрузки" : "Когда свободен"} form={form} setForm={setForm} />;
  const price = (
    <label className="field" key="price">
      <span>Ставка от, сом</span>
      <span className="input-wrap">
        <input name="price" type="number" min="0" placeholder="Любая" aria-label="Ставка от, сом" value={form.price} onChange={set("price")} />
      </span>
    </label>
  );
  const verified = (
    <label className="check-label verified-filter" key="verified">
      <input type="checkbox" name="verified" checked={!!form.verified} onChange={(e) => setForm((f) => ({ ...f, verified: e.target.checked }))} />
      Только проверенные
    </label>
  );

  if (home)
    return (
      <>
        <div className="fields-row first-row">
          {origin}
          {radius}
          {destination}
        </div>
        <div className="fields-row second-row">
          {category}
          {body}
          {available}
        </div>
        {extras && (
          <div className="extra-filters">
            {capacity}
            {volume}
            {price}
            {verified}
          </div>
        )}
      </>
    );

  // one `.filter-grid` of equal columns, as in the design; transport has «Тип кузова» where loads have «Тип груза»
  return (
    <>
      <div className="filter-grid">
        {origin}
        {radius}
        {destination}
        {loads ? cargo : body}
        {category}
        {capacity}
        {volume}
        {available}
        {price}
      </div>
    </>
  );
}
